import { useState, useMemo } from 'react';
import RouteError from '@/components/ui/route-error';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { queryClient } from '@/lib/query-client';
import { useAppStore } from '@/stores/app-store';
import { useTeamMembers, getTeamMembersQueryOptions } from '@/features/team/api/team-api';
import DashboardRouteLayout from '@/app/components/dashboard-route-layout';
import DashboardPageShell from '@/app/components/dashboard-page-shell';
import InviteMemberModal from '@/features/team/components/invite-member-modal';
import EditMemberModal from '@/features/team/components/edit-member-modal';
import { useDisclosure } from '@/components/ui/lib/use-disclosure';
import { Avatar } from '@/components/ui/lib/avatar';
import { Badge } from '@/components/ui/lib/badge';
import { Button } from '@/components/ui/lib/button';
import { HStack } from '@/components/ui/lib/hstack';
import PrimaryButton from '@/components/ui/primary-button';
import { Flex } from '@/components/ui/lib/flex';
import { Input } from '@/components/ui/lib/input';
import { Skeleton } from '@/components/ui/lib/skeleton';
import { Text } from '@/components/ui/lib/text';
import DataTable from '@/components/ui/data-table';
import { DICTIONARY } from '@/config/dictionary';
import { getRoleLabel } from '@/config/role-labels';
import { requireAuth } from '@/utils/require-auth-before-load';
import { LuPencil } from 'react-icons/lu';
import type { DataTableColumnDef } from '@/types/data-table';
import type { TeamMember, TeamRole } from '@/types/team';

const d = DICTIONARY.teamPage;

const buildTeamColumnDefs = (
  roles: TeamRole[],
  currentUserId: string | null,
  onEdit: (member: TeamMember) => void,
): DataTableColumnDef<TeamMember>[] => [
  {
    key: 'name',
    header: d.columns.name,
    cell: (member) => {
      const initials = `${member.firstName[0] ?? ''}${member.lastName[0] ?? ''}`.toUpperCase();
      return (
        <HStack gap="3">
          <Avatar.Root size="sm" bg="var(--accent-subtle)" color="var(--accent)">
            <Avatar.Fallback>{initials}</Avatar.Fallback>
          </Avatar.Root>
          <Text fontSize="sm" fontWeight="500" className="text-base">
            {member.displayName}
          </Text>
        </HStack>
      );
    },
  },
  {
    key: 'email',
    header: d.columns.email,
    cell: (member) => (
      <Text fontSize="sm" className="text-muted">
        {member.email}
      </Text>
    ),
  },
  {
    key: 'role',
    header: d.columns.role,
    cell: (member) => {
      const role = roles.find((r) => r.id === member.roleId);
      const roleLabel = getRoleLabel(role?.name);
      return (
        <Badge variant="plain" size="sm" className="bg-accent-subtle text-accent font-medium">
          {roleLabel}
        </Badge>
      );
    },
  },
  {
    key: 'actions',
    header: '',
    width: '20',
    textAlign: 'right',
    cell: (member) => {
      const canEdit = member.id !== currentUserId;
      if (!canEdit) return null;
      return (
        <Button
          size="xs"
          variant="ghost"
          className="text-accent"
          onClick={() => {
            onEdit(member);
          }}
        >
          <LuPencil />
          {DICTIONARY.common.edit}
        </Button>
      );
    },
  },
];

const DashboardTeamPage = () => {
  const navigate = useNavigate();
  const { search: searchParam } = useSearch({ from: '/team' });
  const search = searchParam ?? '';
  const { open: inviteOpen, onOpen: openInvite, onClose: closeInvite } = useDisclosure();
  const { open: editOpen, onOpen: openEdit, onClose: closeEdit } = useDisclosure();
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);

  const handleSearchChange = (value: string) => {
    void navigate({
      to: Route.fullPath,
      search: { search: value.length > 0 ? value : undefined },
      replace: true,
    });
  };

  const selectedWholesalerId = useAppStore((s) => s.selectedWholesalerId);
  const initialWholesalerId = useAppStore((s) => s.currentUser?.wholesalers[0]?.id ?? null);
  const wholesalerId = selectedWholesalerId ?? initialWholesalerId ?? '';
  const currentUserId = useAppStore((s) => s.currentUser?.id ?? null);

  const { data, isLoading } = useTeamMembers(wholesalerId || null);

  const members = useMemo(() => data?.members ?? [], [data]);
  const roles = useMemo(() => data?.roles ?? [], [data]);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const roleName = roles.find((r) => r.id === m.roleId)?.name ?? '';
      return (
        m.displayName.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        roleName.toLowerCase().includes(q)
      );
    });
  }, [members, roles, search]);

  const handleEdit = (member: TeamMember) => {
    setEditingMember(member);
    openEdit();
  };

  const columnDefs = buildTeamColumnDefs(roles, currentUserId, handleEdit);

  return (
    <DashboardRouteLayout>
      <DashboardPageShell title={d.heading} description={d.description}>
        <Flex justify="space-between" align="center" mt="-2" mb="4" gap="3">
          <Input
            size="sm"
            maxW="320px"
            placeholder={d.searchPlaceholder}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleSearchChange(e.target.value);
            }}
          />
          <PrimaryButton size="sm" onClick={openInvite}>
            {d.inviteButton}
          </PrimaryButton>
        </Flex>

        {isLoading ? (
          <Flex direction="column" gap="3">
            <Skeleton height="40px" rounded="md" />
            <Skeleton height="52px" rounded="md" />
            <Skeleton height="52px" rounded="md" />
            <Skeleton height="52px" rounded="md" />
          </Flex>
        ) : members.length === 0 ? (
          <Text fontSize="sm" className="text-muted" textAlign="center" py="8">
            {d.emptyState}
          </Text>
        ) : (
          <DataTable
            columnDefs={columnDefs}
            rowData={filteredMembers}
            getRowKey={(member) => member.id}
            emptyState={
              <Text fontSize="sm" className="text-muted">
                {d.noResults}
              </Text>
            }
          />
        )}
      </DashboardPageShell>

      {wholesalerId ? (
        <InviteMemberModal open={inviteOpen} onClose={closeInvite} wholesalerId={wholesalerId} />
      ) : null}

      {wholesalerId ? (
        <EditMemberModal
          open={editOpen}
          onClose={closeEdit}
          member={editingMember}
          roles={roles}
          wholesalerId={wholesalerId}
        />
      ) : null}
    </DashboardRouteLayout>
  );
};

const teamSearchSchema = z.object({
  search: z.string().optional(),
});

export const Route = createFileRoute('/team')({
  validateSearch: teamSearchSchema,
  beforeLoad: requireAuth,
  loader: async () => {
    const { selectedWholesalerId, currentUser } = useAppStore.getState();
    const wholesalerId = selectedWholesalerId ?? currentUser?.wholesalers[0]?.id;
    if (wholesalerId == null) return null;
    await queryClient.prefetchQuery(getTeamMembersQueryOptions(wholesalerId));
    return null;
  },
  component: DashboardTeamPage,
  errorComponent: RouteError,
});
